/**
 * Research registry — the directions the laboratory actually works on.
 * Same publication boundary as projects.ts: purpose and observable
 * properties; no mechanisms of layers that are not published yet.
 */

export interface Research {
  slug: string;
  title: string;
  area: string;
  hypothesis: string;
  problem: string;
  state: 'active' | 'maturing' | 'published-artifact';
  stateLabel: string;
  methods: string[];
  findings: string[];
  /** Slugs of related projects. */
  projects: string[];
  /** Slugs of related research. */
  related: string[];
  /** Ids of knowledge-graph nodes this research touches. */
  knowledge: string[];
}

export const research: Research[] = [
  {
    slug: 'agent-native-software',
    title: 'Agent-Native Software',
    area: 'Multi-Agent Systems',
    hypothesis:
      'When agents are treated as first-class users of software — with negotiation, ownership and economy built into the runtime — coordination structures emerge that do not need a central orchestrator.',
    problem:
      'Today’s agent frameworks bolt agents onto human-shaped software: one orchestrator, a task queue, and brittle glue. That shape caps autonomy at the orchestrator’s imagination.',
    state: 'published-artifact',
    stateLabel: 'Open artifact published',
    methods: [
      'Runtime design: relationship negotiation between agents',
      'Resource economics inside the runtime',
      'Byzantine agreement between untrusted nodes',
      'Recursive organization: groups folding into composite agents',
    ],
    findings: [
      'A working MIT-licensed runtime (Agent Native Universe) demonstrating negotiation, economy and recursive organization',
      'Fold/unfold of agent groups is expressible as a first-class runtime operation',
    ],
    projects: ['agent-native-universe'],
    related: ['multi-agent-consensus', 'agent-orchestration'],
    knowledge: ['agent-native', 'multi-agent', 'agent-economy'],
  },
  {
    slug: 'multi-agent-consensus',
    title: 'Consensus and Economies for Agents',
    area: 'Distributed Systems',
    hypothesis:
      'Self-organizing agent collectives need the same primitives as human markets and distributed databases: scarce resources, negotiated agreements, and consensus that survives faulty or hostile members.',
    problem:
      'Without scarcity and agreement, agent swarms either stall in deadlock or amplify errors. Most frameworks ignore both and hope the LLM sorts it out.',
    state: 'published-artifact',
    stateLabel: 'Open artifact published',
    methods: [
      'Byzantine agreement protocols between agent nodes',
      'Resource pricing and budget propagation through agent groups',
      'Failure injection: hostile and faulty agents in the collective',
    ],
    findings: [
      'Byzantine agreement is practical at the scale of agent working groups',
      'A resource economy measurably shapes agent behaviour without explicit programming',
    ],
    projects: ['agent-native-universe'],
    related: ['agent-native-software'],
    knowledge: ['consensus', 'agent-economy', 'multi-agent'],
  },
  {
    slug: 'knowledge-graphs',
    title: 'Living Knowledge Models',
    area: 'Knowledge Systems',
    hypothesis:
      'A domain becomes teachable and searchable when it is represented as a connected model — entities, relations, provenance — that updates as the domain moves, instead of a static index.',
    problem:
      'Documents pile up; meaning does not. Retrieval without structure returns fragments; structure without provenance returns confident nonsense.',
    state: 'active',
    stateLabel: 'Active — in production use',
    methods: [
      'Entity and relation extraction into a graph model',
      'Semantic retrieval over graph neighbourhoods',
      'Provenance tracking: every node knows where it came from',
      'Continuous updates as source material changes',
    ],
    findings: [
      'Graph-shaped knowledge outperforms flat retrieval for curriculum-scale domains',
      'Provenance is what turns retrieval into something a product can trust',
    ],
    projects: ['knowledgebase-ai', 'studyninja'],
    related: ['adaptive-mastery'],
    knowledge: ['knowledge-graph', 'semantic-retrieval', 'provenance'],
  },
  {
    slug: 'adaptive-mastery',
    title: 'Adaptive Mastery Models',
    area: 'Human–Machine Learning',
    hypothesis:
      'A student who sees exactly where they are stuck, and receives the smallest next step that produces a win, recovers their learning trajectory — motivation is born from success, not gifted.',
    problem:
      'Linear courses assume nobody falls behind. Everybody falls behind. The interesting problem is diagnosis and recovery, not content delivery.',
    state: 'active',
    stateLabel: 'Active — in production use',
    methods: [
      'Mastery estimation over a subject knowledge graph',
      'Gap diagnostics: locating the failed prerequisite, not the failed exercise',
      'Next-step selection tuned for visible small wins',
    ],
    findings: [
      'Gap-first sequencing changes learner behaviour within days, not months',
      'Mastery must be modelled on the graph, not on the exercise list',
    ],
    projects: ['studyninja', 'knowledgebase-ai'],
    related: ['knowledge-graphs'],
    knowledge: ['mastery', 'gap-diagnostics', 'adaptive-paths'],
  },
  {
    slug: 'compensated-numerics',
    title: 'Computation That Survives Singularities',
    area: 'Numerical Foundations',
    hypothesis:
      'Numerical pipelines can be built so that division by zero, overflow and precision loss degrade the result gracefully instead of destroying it.',
    problem:
      'One NaN poisons a tensor; one overflow kills a training run. The standard answer is defensive clipping everywhere — which silently changes the mathematics.',
    state: 'maturing',
    stateLabel: 'Maturing — library published openly',
    methods: [
      'A computation library (Balansis) exercised as the numerical base of the other layers',
      'Stress suites around singular points and extreme magnitudes',
    ],
    findings: [
      'The approach holds under the laboratory’s stress suites, and the library carrying it is public — the mechanism can be read, not taken on trust',
    ],
    projects: ['balansis', 'magicbrain'],
    related: ['grown-networks'],
    knowledge: ['numerical-stability', 'compensated-arithmetic'],
  },
  {
    slug: 'grown-networks',
    title: 'Grown, Not Designed, Networks',
    area: 'Neuromorphic AI',
    hypothesis:
      'Network structure that develops — the way biological tissue does — can reach behaviours that hand-designed architectures reach only by brute force.',
    problem:
      'Architecture search treats structure as a hyperparameter. Biology treats it as the product of development. Those are very different search spaces.',
    state: 'maturing',
    stateLabel: 'Maturing — mechanism published',
    methods: [
      'Spiking (impulse-based) network substrate',
      'Learning rules local to the connection',
      'Structural development instead of fixed topology',
    ],
    findings: [
      'Local learning plus developing structure trains at research scale, and the mechanism is now open: the library is on GitHub under AGPL-3.0',
      'Measured honestly, the substrate beats a unigram baseline on character-level text but does not reach a bigram model — the runs and the numbers are published alongside the playground',
    ],
    projects: ['magicbrain'],
    related: ['compensated-numerics'],
    knowledge: ['spiking-networks', 'local-learning', 'structural-growth'],
  },
  {
    slug: 'agent-orchestration',
    title: 'Agents Operating Real Infrastructure',
    area: 'Applied Multi-Agent Systems',
    hypothesis:
      'Multi-agent teams can carry real operational work — deployment, diagnostics, review — if their autonomy is gated by confidence and their knowledge is shared through a common graph.',
    problem:
      'Demos of agent teams are easy; agents that touch production infrastructure and survive contact with it are not.',
    state: 'active',
    stateLabel: 'Active — internal systems',
    methods: [
      'Agent teams attached to live infrastructure with confidence-gated autonomy',
      'Shared knowledge graph as the coordination medium',
      'Escalation to humans as a first-class protocol step',
    ],
    findings: [
      'Confidence gating, not capability, is the binding constraint on useful autonomy',
    ],
    projects: ['knowledgebase-ai'],
    related: ['agent-native-software', 'knowledge-graphs'],
    knowledge: ['orchestration', 'autonomy-gating', 'multi-agent'],
  },
  {
    slug: 'unified-wholeness',
    title: 'Unified Wholeness Theory',
    area: 'Theory',
    hypothesis:
      'A theoretical research line is worth running inside an engineering laboratory only if every claim is falsifiable and every experiment is registered.',
    problem:
      'Theory drifts into storytelling the moment its claims stop being checkable. Keeping a theory honest requires engineering discipline.',
    state: 'maturing',
    stateLabel: 'Preprint package in preparation',
    methods: [
      'Registry of experiments with automated tests over claims',
      'Preprint package prepared for open publication',
    ],
    findings: [
      'The experiment-registry discipline transfers well from software testing to theoretical work',
    ],
    projects: ['uwt'],
    related: [],
    knowledge: ['falsifiability', 'experiment-registry'],
  },
];

export const researchBySlug = new Map(research.map((r) => [r.slug, r]));
export const isResearchSlug = (s: string) => researchBySlug.has(s);
